import { ChangeDetectionStrategy, Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { OpportunitiesForStage } from '../pipeline-board/interfaces';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { PipelineBoardService } from '../pipeline-board-service/pipeline-board.service';
import { WorkStates } from '@vendasta/rx-utils/work-state';
import { Opportunity } from '@vendasta/sales-opportunities';
import { Observable, ReplaySubject } from 'rxjs';
import { filter, map, mergeMap, publishReplay, refCount, switchMap, take } from 'rxjs/operators';
import { StageInterface } from '@vendasta/sales-opportunities';

@Component({
  selector: 'sales-ui-pipeline-stage',
  templateUrl: './stage.component.html',
  styleUrls: ['./stage.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class StageComponent {
  @Output() draggedOpportunityId = new EventEmitter<string>();
  @Output() selectedOpportunity = new EventEmitter<Opportunity>();

  _stage: StageInterface;
  state: WorkStates<Opportunity[]>;
  readonly opportunities$: Observable<Opportunity[]>;
  readonly opportunitiesWithStage$: Observable<OpportunitiesForStage>;
  private readonly stage$$ = new ReplaySubject<StageInterface>(1);

  constructor(readonly store: PipelineBoardService) {
    this.opportunities$ = this.stage$$.asObservable().pipe(
      filter((s) => !!s),
      take(1),
      mergeMap((stage) => this.store.opportunitiesInStage$(stage)),
      publishReplay(1),
      refCount(),
    );

    this.opportunitiesWithStage$ = this.stage$$.pipe(
      switchMap((stage) =>
        this.opportunities$.pipe(
          map(
            (opps) =>
              <OpportunitiesForStage>{
                stage: stage,
                opportunities: opps,
              },
          ),
        ),
      ),
    );
  }

  @Input() set stage(stage: StageInterface) {
    this.stage$$.next(stage);
    this._stage = stage;
    this.state = this.store.stageState(stage.stageId);
  }

  drop(event: CdkDragDrop<OpportunitiesForStage>): void {
    if (event.previousContainer !== event.container) {
      const col: OpportunitiesForStage = event.previousContainer.data;
      const opp = col.opportunities[event.previousIndex];
      this.draggedOpportunityId.emit(opp.opportunityId);
      const newProb = event.container.data.stage.probability;

      // TODO: Disable movement until update finishes.
      this.store.updateProbability(opp, newProb);
      this.store.doLoadMoreIfNecessary(
        event.previousContainer.data.opportunities.length,
        event.previousContainer.data.stage,
      );
    }
  }

  @HostListener('scroll', ['$event'])
  getNextPage(event: any): void {
    if (event.target.offsetHeight + event.target.scrollTop >= event.target.scrollHeight) {
      this.store.doGetNextPageFor(this._stage);
    }
  }

  handleOpportunitySelected(opportunity: Opportunity): void {
    this.selectedOpportunity.emit(opportunity);
  }
}
